import { FC, useState } from 'react';
import {
  cn,
  Tag,
  Badge,
  FaIcon,
  ResourceTexts,
  TResourceGroupType,
} from '@fe-learner-common';
import { faFile } from '@fortawesome/pro-regular-svg-icons/faFile';
import { faPlay } from '@fortawesome/pro-regular-svg-icons/faPlay';
import { faLink } from '@fortawesome/pro-regular-svg-icons/faLink';
import { faVolume } from '@fortawesome/pro-regular-svg-icons/faVolume';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';

type ResourceProps = {
  className?: string;
  resourceId: string;
  title?: string | null;
  groupType: TResourceGroupType;
  thumbnail?: string | null;
  tags: string[];
  isNew?: boolean;
  texts: ResourceTexts;
};

const thumbnailIconMap: Record<
  TResourceGroupType,
  { icon: IconDefinition; className: string }
> = {
  video: {
    icon: faPlay,
    className: 'lf:text-neutral-0',
  },
  image: {
    icon: faFile,
    className: 'lf:text-neutral-0',
  },
  audio: {
    icon: faVolume,
    className: 'lf:text-neutral-500',
  },
  link: {
    icon: faLink,
    className: 'lf:text-neutral-500',
  },
  document: {
    icon: faFile,
    className: 'lf:text-neutral-500',
  },
  other: {
    icon: faFile,
    className: 'lf:text-neutral-500',
  },
};

type ThumbnailProps = {
  type: TResourceGroupType;
  thumbnail?: string | null;
};
const Thumbnail: FC<ThumbnailProps> = ({ type, thumbnail }) => {
  const [isError, setIsError] = useState(false);
  const iconDetails = thumbnailIconMap[type] || thumbnailIconMap.other;
  return (
    <div className="lf:relative lf:aspect-video lf:h-[40px] lf:rounded-lg lf:bg-neutral-100 lf:group-hover:bg-neutral-50">
      {thumbnail && !isError && (
        <img
          src={thumbnail}
          alt=""
          className="lf:h-full lf:w-full lf:rounded-lg lf:object-cover"
          onError={() => setIsError(true)}
        />
      )}
      {isError && (
        <FaIcon
          icon={iconDetails.icon}
          width={16}
          height={16}
          className={cn(
            'lf:absolute lf:left-1/2 lf:top-1/2 lf:-translate-x-1/2 lf:-translate-y-1/2 lf:transform',
            iconDetails.className,
          )}
        />
      )}
    </div>
  );
};

export const Resource: FC<ResourceProps> = ({
  className,
  resourceId,
  title,
  groupType,
  thumbnail,
  tags,
  isNew,
  texts,
}) => {
  return (
    <div className={cn('lf:flex lf:gap-x-4', className)}>
      <Thumbnail type={groupType} thumbnail={thumbnail} />
      <div className="lf:space-y-1">
        <h4 className="lf:break-all lf:text-start lf:text-label-lg lf:text-neutral-900">
          {title}
        </h4>
        <div className="lf:flex lf:flex-wrap lf:items-center lf:gap-2">
          <span className="lf:text-body-md lf:text-neutral-500">
            {texts?.groupTypes?.[groupType]}
          </span>
          {tags?.length ? (
            <>
              <span className="lf:text-body-md lf:text-neutral-500">·</span>
              {tags.map((tag) => (
                <Tag
                  key={`resource-tag-${resourceId}-${tag}`}
                  className="lf:group-hover:bg-neutral-50"
                >
                  <span>{tag}</span>
                </Tag>
              ))}
            </>
          ) : null}

          {isNew && (
            <div className="lf:flex lf:items-center lf:gap-x-1">
              <Badge size="sm" type="negative" />
              <span className="lf:text-label-sm lf:text-negative-500">
                {texts?.new}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
